import { Children, KeyboardEvent, ReactNode } from 'react';
import { KeyValue } from '@mews/enums';
import { SelectionStrategy } from './types';
import { OptionProps as MappedOptionProps } from './useOptions';

type OptionProps =
    | { options: OptionProps[]; value?: string; disabled?: boolean }
    | { value: string; disabled?: boolean };

export const selectAllFromGroup = (option: OptionProps, selectionStrategy?: SelectionStrategy): string[] => {
    const isLeaf = 'value' in option && !('options' in option);

    if (isLeaf) {
        return option.value && !option.disabled ? [option.value] : [];
    }

    const childrenValues = option.options.reduce((acc, child) => {
        // skip disabled group-level children entirely
        if (child.disabled) return acc;

        return [...acc, ...selectAllFromGroup(child, selectionStrategy)];
    }, [] as string[]);

    const includeGroupValue = selectionStrategy === 'check-strictly' && option.value && !option.disabled;

    return includeGroupValue && option.value ? [option.value, ...childrenValues] : childrenValues;
};

export const toggleGroupSelection = (
    group: OptionProps,
    checked: boolean,
    selected: Set<string>,
    selectionStrategy: SelectionStrategy
): string[] => {
    if (selectionStrategy === 'check-strictly' && group.value) {
        return !group.disabled ? toggleEntrySelection(group.value, checked, selected) : Array.from(selected);
    }

    const groupOptions = selectAllFromGroup(group);
    const groupOptionsSet = new Set(groupOptions);

    return checked
        ? Array.from(new Set([...selected, ...groupOptions]))
        : Array.from(selected).filter((val) => !groupOptionsSet.has(val));
};

export const toggleEntrySelection = (optionValue: string, checked: boolean, selected: Set<string>): string[] => {
    if (checked) {
        selected.add(optionValue);
    } else {
        selected.delete(optionValue);
    }

    return Array.from(selected);
};

export const combineKeydownEvents =
    (...events: ((e: KeyboardEvent<HTMLLIElement>) => void)[]) =>
    (event: KeyboardEvent<HTMLLIElement>) => {
        events.forEach((e) => e(event));
    };

export const handleKeyboardClick = (e: KeyboardEvent<HTMLLIElement>) => {
    if (![KeyValue.Enter, ' '].includes(e.key)) {
        return;
    }

    e.preventDefault();
    e.currentTarget.click();
};

export const handleKeyboardFocusControl = (container: HTMLElement | null) => (e: KeyboardEvent<HTMLLIElement>) => {
    if (![KeyValue.ArrowDown, KeyValue.ArrowUp].includes(e.key as KeyValue) || !container) {
        return;
    }

    e.preventDefault();

    const options: HTMLLIElement[] = Array.from(
        container.querySelectorAll('li[role="option"], li[role="group"]') || []
    );

    const focusedIndex = options.findIndex((el) => el === e.currentTarget);

    const getNextIndex = () => {
        let index = focusedIndex + 1 < options.length ? focusedIndex + 1 : 0;

        while (options[index].parentElement?.getAttribute('aria-expanded') === 'false') {
            index++;

            if (index >= options.length) {
                index = 0;
            }
        }

        return options[index];
    };

    const getPrevIndex = () => {
        let index = focusedIndex - 1 >= 0 ? focusedIndex - 1 : options.length - 1;

        while (options[index].parentElement?.getAttribute('aria-expanded') === 'false') {
            index--;

            if (index < 0) {
                index = options.length - 1;
            }
        }

        return options[index];
    };

    const option = e.key === 'ArrowDown' ? getNextIndex() : getPrevIndex();

    option?.focus();
};

export const handleKeyboardGroupCollapseControl =
    (onToggle: (expand: boolean) => void) => (e: KeyboardEvent<HTMLLIElement>) => {
        if (![KeyValue.ArrowLeft, KeyValue.ArrowRight].includes(e.key as KeyValue)) {
            return;
        }

        e.preventDefault();

        if (e.key === KeyValue.ArrowLeft) {
            onToggle(false);
        } else if (e.key === KeyValue.ArrowRight) {
            onToggle(true);
        }
    };

export const getTextContentOfNode = (node: ReactNode): string => {
    if (typeof node === 'string') {
        return node;
    }

    return node
        ? Children.toArray(node)
              .map((child) => {
                  if (typeof child === 'string' || typeof child === 'number') {
                      return `${child}`;
                  }

                  if ('props' in child) {
                      return getTextContentOfNode(child.props.children);
                  }

                  return getTextContentOfNode(child);
              })
              .join(' ')
        : '';
};

export const getTotalOptionsCount = (options: MappedOptionProps[], selectionStrategy: SelectionStrategy): number => {
    if (selectionStrategy === 'check-strictly') {
        return options.reduce((acc, opt) => {
            const children = 'options' in opt && opt.options ? getTotalOptionsCount(opt.options, selectionStrategy) : 0;
            return acc + 1 + children;
        }, 0);
    }

    return options.reduce((acc, { optionsCount }) => acc + (optionsCount || 1), 0);
};

export const getTotalNotDisabledOptionsCount = (
    options: MappedOptionProps[],
    selectionStrategy: SelectionStrategy
): number => {
    if (selectionStrategy === 'check-strictly') {
        return options.reduce((acc, option) => {
            const currentCount = !option.disabled ? 1 : 0;

            const nestedCount =
                option.isGroup && 'options' in option && option.options
                    ? getTotalNotDisabledOptionsCount(option.options, selectionStrategy)
                    : 0;

            return acc + currentCount + nestedCount;
        }, 0);
    }

    return options.reduce((acc, option) => {
        if (option.isGroup && 'options' in option && option.options) {
            return acc + getTotalNotDisabledOptionsCount(option.options, selectionStrategy);
        }
        return acc + (!option.isGroup && !option.disabled ? 1 : 0);
    }, 0);
};
